/**
 * The list rungs, in one place. Every row-shaped surface reads its height
 * from here — the DataTable's regular row and its header bands, and the
 * property lists' pitch — so a change of mind about how tall a list row is
 * lands as one edit, not a sweep. Each is a plain pixel count; the consumers
 * (virtualizer math, sticky offsets, skeleton mirrors, reserved heights)
 * derive everything else from them.
 *
 * The table rungs are the touch-target rung: a 44px row is a comfortable
 * press on a finger as well as a pointer. The property pitch is the control's
 * own height — a Button already draws a 44px hit halo on a coarse pointer.
 */
export type RowRung =
  | "tableRow"
  | "tableHeader"
  | "tableGroupBand"
  | "propertyRow"
  | "listRow"
  | "control"
  | "compactControl"
  | "line";

export const ROW_RUNGS: Readonly<Record<RowRung, number>> = {
  /** DataTable `regular` data row. `compact` (32), `comfortable` (52) and
   *  `spacious` (56) keep their own rows. */
  tableRow: 44,
  /** DataTable column-header band at the `regular`, `comfortable` and
   *  `spacious` densities; `compact` keeps a 32px band over its 32px rows. */
  tableHeader: 44,
  /** DataTable group-header band, every density — a step under the rows so
   *  a group title reads as a divider, not as another row. */
  tableGroupBand: 36,
  /** `PropertyRow` pitch: the drawer property lists and their fold row. The
   *  row hugs its 32px value control — the property panel is a label column
   *  beside a column of controls, not a list of pressable rows. */
  propertyRow: 32,
  /** A list card's row, an access list's row, a column-settings row: a step
   *  under the table row, so a dense list still reads as rows. */
  listRow: 36,
  /** A `sm` control and the row that hugs one: a nested access row, a
   *  candidate row in a picker, a reorderable settings row. */
  control: 32,
  /** A `xs` control: a toolbar button, a card's label segment, an in-row
   *  action chip, a compact search field. */
  compactControl: 28,
  /** One text line with its air: a fact row in a detail card. */
  line: 24,
};
